import { Folder, Nullable } from '@Models';
import { Result } from '@Redux/type';
import { TYPES } from '@Types';
import {
  FolderRepository,
  RequestFoldersSuccess,
  RequestFolderSuccess,
} from '@Repository/builder';
import { Locale } from '@Repository/locale';
import { inject, injectable } from 'inversify';
import { v4 as uuidv4 } from 'uuid';
import { RequestAction } from '@Repository/RequestAction';
import { IEntitiesAction } from '@Redux/IEntitiesAction';
import foldersSlice from '@Redux/reducers/builder/folders.slice';
import { PayloadAction } from '@reduxjs/toolkit';
import { FolderParams } from '@Builder/myFolder/edit/validate';

@injectable()
export class FoldersAction extends IEntitiesAction<
  Folder,
  RequestFoldersSuccess
> {
  @inject(TYPES.Repository.FolderRepository)
  private _repository!: FolderRepository;
  @inject(TYPES.Locale) private _locale!: Locale;

  public create =
    (item: FolderParams): Result<void> =>
    async (dispatch) => {
      dispatch(this.areLoading(true));

      const folder: Folder = {
        id: uuidv4(),
        name: item.name,
        parentId: item.parentId,
        isShare: item.isShare,
      } satisfies Folder as Folder;

      const response = await this._repository.create({
        folder: folder,
        locale: null,
      });

      if (this.handleFailure(response, dispatch)) return;

      const success = response as RequestFolderSuccess;
      window.location.href = `/${this._locale.getlocale()}/builder/folders/${
        success.payload.id
      }`;
    };

  protected fetchDataSuccess(item: Folder[]): PayloadAction<Folder[]> {
    return foldersSlice.actions.setFolders(item);
  }

  protected cacheDataSuccess(item: Folder[]): PayloadAction<Folder[]> {
    return foldersSlice.actions.setFolders(item);
  }

  protected haveError(message: string): PayloadAction<string> {
    return foldersSlice.actions.setError(message);
  }

  protected areLoading(bool: boolean): PayloadAction<boolean> {
    return foldersSlice.actions.setLoading(bool);
  }

  protected async getItems(): Promise<RequestAction> {
    return this._repository.getFolders({ locale: null });
  }

  protected async getCachedItems(): Promise<Nullable<Array<Folder>>> {
    return this._repository.getAllCached({ locale: null });
  }
}
